import React from "react";
import {
  ShieldCheck,
  Clock,
  HeartHandshake,
  Award,
  Users,
  MapPin,
} from "lucide-react";
import CTASection from "@/components/CTASection";
import { COMPANY } from "@/data/site";

const ABOUT_IMG = "https://images.pexels.com/photos/9551366/pexels-photo-9551366.jpeg";
const LIFESTYLE_IMG = "https://images.pexels.com/photos/18071786/pexels-photo-18071786.jpeg";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    text: "Fully licensed and insured Massachusetts plumbers. Every job done to code, every time.",
  },
  {
    icon: Clock,
    title: "Fast Response",
    text: "Quick, professional response — including weekend emergency service when you need us most.",
  },
  {
    icon: HeartHandshake,
    title: "Honest Service",
    text: "Upfront pricing, no surprise fees. We explain the problem clearly and only fix what needs fixing.",
  },
  {
    icon: Award,
    title: "Quality Workmanship",
    text: "Over 10 years of experience. Clean, careful work that respects your home or business.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              About us
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-[#0F172A] tracking-tight leading-[1.05]">
              A local plumbing company you can actually count on.
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">
              Boston Express Plumbing and Heating LLC has been proudly serving
              homeowners and businesses in Stoughton, MA for more than a decade.
              We built our company on quality work, fast response times, and
              honest, transparent service — the kind of plumber you'd recommend
              to your neighbor without hesitation.
            </p>
          </div>
          <div className="relative">
            <img
              src={ABOUT_IMG}
              alt="Professional water heater installation"
              className="w-full h-[360px] lg:h-[460px] object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-slate-50 py-20 lg:py-28" data-testid="about-story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              Our story
            </span>
            <h2 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
              Built locally. Trusted in Stoughton.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-slate-700 leading-relaxed">
            <p>
              Based out of Stoughton, MA, we started Boston Express Plumbing
              and Heating with a simple goal: provide skilled, dependable
              plumbing service to the community we live and work in. Over the
              years we've grown into a trusted name in Stoughton, handling
              everything from emergency burst pipes at 2 AM to full commercial
              bathroom remodels.
            </p>
            <p>
              We're proud to be a fully licensed and insured Massachusetts
              plumbing company. Our technicians are trained, equipped, and
              committed to doing the job right the first time — and to leaving
              your home cleaner than we found it.
            </p>
            <p>
              Whether you need emergency drain cleaning, a new water heater
              installed, or a long-standing leak finally fixed, we'd be
              honored to earn your trust.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white py-20 lg:py-28" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
              What we stand for
            </span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              Our values
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {VALUES.map((v, i) => (
              <div
                key={i}
                data-testid={`value-card-${i}`}
                className="bg-white p-8"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#0284C7] text-white">
                  <v.icon className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#0F172A]">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {v.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS + IMAGE */}
      <section className="bg-[#0F172A] text-white py-20 lg:py-28" data-testid="about-stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <img
            src={LIFESTYLE_IMG}
            alt="Modern bathroom — quality workmanship"
            className="w-full h-[360px] lg:h-[480px] object-cover"
            loading="lazy"
          />
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              A decade of doing it right.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              We measure success in repeat customers, neighbor referrals, and
              the long-term reliability of every install we complete.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { icon: Award, big: "10+", small: "Years experience" },
                { icon: MapPin, big: "Stoughton", small: "Local service" },
                { icon: Users, big: "1000s", small: "Jobs completed" },
              ].map((s, i) => (
                <div key={i} data-testid={`about-stat-${i}`}>
                  <s.icon className="h-5 w-5 text-[#0284C7]" strokeWidth={1.5} />
                  <div className="mt-3 font-display text-3xl lg:text-4xl font-black">
                    {s.big}
                  </div>
                  <div className="mt-1 text-xs tracking-[0.12em] uppercase text-slate-400 font-semibold">
                    {s.small}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title={`Work with ${COMPANY.short}`}
        subtitle="Friendly, reliable plumbing service in your neighborhood. Call us or request a free quote today."
        variant="light"
      />
    </div>
  );
}
